/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useEffect, useState } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import {
  MaterialGroupingName,
  MaterialGroupName,
  UnitType,
} from "src/store/api/generatedApi";
import {
  buildMaterialColours,
  defaultLayout,
  fractionTickFormatting,
  generalTickFormatting,
  getMaterialColour,
  setSelectedDateFromPlotClick,
} from "../plotting";
import { GroupedInventorySeries, InventoryPlotType } from "../types";
import { TFunction } from "i18next";
import { sorted } from "helpers";

interface InventoryPlotProps {
  inventorySeries: GroupedInventorySeries;
  selectedGrouping: MaterialGroupingName | null;
  selectedPlotType: InventoryPlotType;
  selectedMaterials: MaterialGroupName[];
  setSelectedDate: (date: Date) => void;
}

export const InventoryPlot = ({
  inventorySeries,
  selectedGrouping,
  selectedPlotType,
  selectedMaterials,
  setSelectedDate,
}: InventoryPlotProps) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);

  const initialFigData = buildInitialFigData(inventorySeries, t);

  const [figure, setFigure] = useState(() => ({
    data: initialFigData,
    layout: updateLayout(defaultLayout, selectedPlotType, t, units),
  }));

  useEffect(() => {
    const newData = buildInitialFigData(inventorySeries, t);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- merges previous layout state; not purely computed
    setFigure({
      data: updateFigData(
        newData,
        selectedGrouping,
        selectedPlotType,
        selectedMaterials
      ),
      layout: updateLayout(figure.layout, selectedPlotType, t, units),
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- figure.layout intentionally excluded: effect calls setFigure, including it causes infinite loop
  }, [
    inventorySeries,
    selectedGrouping,
    selectedPlotType,
    selectedMaterials,
    t,
    units,
  ]);

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={figure.data}
        layout={figure.layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
      <LegendHint />
    </div>
  );
};

const buildInitialFigData = (
  groupedInventorySeries: GroupedInventorySeries,
  t: TFunction
): Partial<PlotData>[] => {
  const materialColours = buildMaterialColours(
    groupedInventorySeries.values.map((item) => ({
      groupingName: item[0].materialGroupingName,
      groupName: item[0].materialGroupName,
    }))
  );

  const sortedSeries = sorted(groupedInventorySeries.values, ([key]) => [
    Object.keys(groupedInventorySeries.availableMaterialGroupings).findIndex(
      (materialGrouping) => materialGrouping === key.materialGroupingName
    ),
    groupedInventorySeries.availableMaterialGroupings[
      key.materialGroupingName
    ]?.findIndex((materialGroup) => materialGroup === key.materialGroupName) ??
      -1,
  ]);

  return sortedSeries.flatMap(([key, series]) => {
    const colour = getMaterialColour(
      materialColours,
      key.materialGroupingName,
      key.materialGroupName
    );
    return [
      {
        x: series.date,
        y: series.inventory_mass,
        type: "scatter",
        mode: "lines+markers",
        visible: false,
        name: t(key.materialGroupName),
        line: {
          color: colour,
        },
        marker: {
          color: colour,
        },
        customdata: [key.materialGroupingName, "mass", key.materialGroupName],
        stackgroup: `${key.materialGroupingName}_mass`,
      },
      {
        x: series.date,
        y: series.inventory_fraction,
        type: "scatter",
        mode: "lines+markers",
        visible: false,
        name: t(key.materialGroupName),
        line: {
          color: colour,
        },
        marker: {
          color: colour,
        },
        customdata: [
          key.materialGroupingName,
          "fraction",
          key.materialGroupName,
        ],
        stackgroup: `${key.materialGroupingName}_fraction`,
      },
    ];
  });
};

const updateFigData = (
  figData: Partial<PlotData>[],
  selectedGrouping: MaterialGroupingName | null,
  selectedPlotType: InventoryPlotType,
  selectedMaterials: MaterialGroupName[]
): Partial<PlotData>[] => {
  return figData.map((trace) => ({
    ...trace,
    visible:
      trace.customdata?.[0] === selectedGrouping &&
      trace.customdata?.[1] === selectedPlotType &&
      selectedMaterials.includes(trace.customdata?.[2] as MaterialGroupName),
  }));
};

const updateLayout = (
  currentLayout: Partial<Layout>,
  selectedPlotType: InventoryPlotType,
  t: (key: string) => string,
  units: (key: UnitType) => string
): Partial<Layout> => {
  const yaxisTitle =
    selectedPlotType === "mass"
      ? `${t("inventory")}${units("mass")}`
      : `${t("inventory")} (%)`;

  return {
    ...currentLayout,
    legend: {
      ...currentLayout.legend,
      traceorder: "normal",
    },
    yaxis: {
      ...currentLayout.yaxis,
      title: { text: yaxisTitle },
      tickformat:
        selectedPlotType === "fraction"
          ? fractionTickFormatting
          : generalTickFormatting,
    },
  };
};
